// HCEN Patient Website — Refer & Earn
// Mirrors lib/screens/referral/refer_and_earn_screen.dart (Flutter app).
// Shows the user's referral code + stats funnel + share buttons + terms.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdContentCopy, MdShare, MdRedeem, MdCheckCircle, MdHourglassEmpty,
  MdHowToReg, MdEventAvailable, MdAttachMoney,
} from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function ReferEarnPage() {
  const navigate = useNavigate();
  const { user, showToast } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let alive = true;
    api.myReferrals({ usersId: user.patientId, phone: user.phoneDigits || user.phone })
      .then(r => {
        if (!alive) return;
        if (r.error) setError(r.error);
        else setData(r.data);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [user]);

  if (!user) return (
    <div className="page empty fade-in">
      <div className="empty-icon">🎁</div>
      <div className="empty-title">Login to access Refer &amp; Earn</div>
      <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/login')}>
        Login / Sign Up
      </button>
    </div>
  );

  if (loading) return (
    <div className="page fade-in" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
      Loading your referral dashboard…
    </div>
  );

  if (error || !data) return (
    <div className="page fade-in" style={{ padding: 24 }}>
      <div className="glass glass-p" style={{ borderColor: 'rgba(239,68,68,0.4)' }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Could not load referrals</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{error || 'Please try again.'}</div>
      </div>
    </div>
  );

  const { code, stats = {}, shareMessage, shareLink, terms = [], rewardAmount = 50, cap = 10 } = data;
  const credited  = stats.credited  || 0;
  const earned    = credited * rewardAmount;
  const reachedCap = credited >= cap;

  const copy = async (text, label = 'Copied') => {
    try { await navigator.clipboard.writeText(text); showToast(`${label} ✅`, 'success'); }
    catch { showToast('Copy failed — long-press to copy manually', 'error'); }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'HCEN Refer & Earn', text: shareMessage, url: shareLink }); return; }
      catch (_) { /* user cancelled */ }
    }
    copy(shareMessage, 'Share message copied');
  };

  return (
    <div className="page fade-in" style={{ maxWidth: 560 }}>
      {/* Hero */}
      <div className="glass glass-p" style={{
        marginBottom: 16, textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(211,47,47,0.10), rgba(102,187,106,0.10))',
        borderColor: 'rgba(211,47,47,0.25)',
      }}>
        <div style={{ fontSize: 36, marginBottom: 4 }}>🎁</div>
        <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>
          Earn ₹{rewardAmount} per referral
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Invite friends to HCEN. When they complete their first hospital check-in,<br/>
          you both win — they get the platform fee waived, you get ₹{rewardAmount}.
        </div>
      </div>

      {/* Code card */}
      <div className="glass glass-p" style={{ marginBottom: 16, textAlign: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 1, marginBottom: 8 }}>
          YOUR REFERRAL CODE
        </div>
        <div style={{
          fontSize: 30, fontWeight: 900, letterSpacing: 4,
          color: 'var(--red, #d32f2f)', marginBottom: 14, fontFamily: 'monospace',
        }}>
          {code || '—'}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => copy(code, 'Code copied')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MdContentCopy size={14} /> Copy code
          </button>
          <button className="btn btn-primary btn-sm" onClick={share}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MdShare size={14} /> Share invite
          </button>
        </div>
        {shareLink && (
          <button onClick={() => copy(shareLink, 'Link copied')}
            style={{
              marginTop: 12, background: 'transparent', border: 'none',
              color: 'var(--primary, #d32f2f)', fontSize: 12, cursor: 'pointer',
              textDecoration: 'underline', wordBreak: 'break-all',
            }}>
            {shareLink}
          </button>
        )}
      </div>

      {/* Earnings card */}
      <div className="glass glass-p" style={{
        marginBottom: 16,
        background: reachedCap
          ? 'linear-gradient(135deg, rgba(245,158,11,0.10), rgba(245,158,11,0.04))'
          : 'linear-gradient(135deg, rgba(102,187,106,0.10), rgba(102,187,106,0.04))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)' }}>TOTAL EARNED</div>
          <MdAttachMoney size={20} style={{ color: '#66bb6a' }} />
        </div>
        <div style={{ fontSize: 28, fontWeight: 900 }}>₹{earned.toLocaleString('en-IN')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          {credited} of max {cap} successful referrals
          {reachedCap && ' · Cap reached 🎉'}
        </div>
      </div>

      {/* Funnel */}
      <div className="glass glass-p" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 14 }}>Referral funnel</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
          <FunnelCell icon={<MdHourglassEmpty />} label="Pending"   value={stats.pending   || 0} color="#fbbf24" />
          <FunnelCell icon={<MdHowToReg />}        label="Signed up" value={stats.signed_up || 0} color="#42a5f5" />
          <FunnelCell icon={<MdEventAvailable />}  label="Booked"    value={stats.booked    || 0} color="#a78bfa" />
          <FunnelCell icon={<MdCheckCircle />}     label="Credited"  value={credited}              color="#66bb6a" />
        </div>
      </div>

      {/* Terms */}
      {terms.length > 0 && (
        <div className="glass glass-p" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <MdRedeem size={18} style={{ color: 'var(--primary, #d32f2f)' }} />
            How it works
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.7 }}>
            {terms.map((t, i) => <li key={i} style={{ marginBottom: 4 }}>{t}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function FunnelCell({ icon, label, value, color }) {
  return (
    <div style={{
      padding: '14px 12px', borderRadius: 12,
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>{label.toUpperCase()}</span>
        <span style={{ color, fontSize: 18, display: 'flex' }}>{icon}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 900 }}>{value}</div>
    </div>
  );
}
